import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { PlayCircle, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';
import { replayWalkthrough } from '../Walkthrough/Walkthrough';

/**
 * Profile-page section: Replay Walkthrough + Delete Account.
 */
export default function AccountActions() {
  const { api, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleReplay = () => {
    toast.message('Restarting walkthrough...');
    replayWalkthrough(api, refreshUser);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.delete('/users/me');
      const days = res?.data?.expectedFulfilmentByDays || 30;
      toast.success(
        `Your account has been deactivated. We'll fully erase your personal data within ${days} days.`,
        { duration: 7000 }
      );
      logout();
      navigate('/login');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete account'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card className="border-0 shadow-sm" data-testid="account-actions-section">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900 mb-3">Help & Tips</h3>
            <Button
              variant="outline"
              className="w-full sm:w-auto rounded-full h-11"
              onClick={handleReplay}
              data-testid="replay-walkthrough-btn"
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              Replay walkthrough
            </Button>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <h3 className="text-base font-semibold text-slate-900 mb-1">Delete your account</h3>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed">
              You can request deletion at any time. We&apos;ll <strong>deactivate your account immediately</strong> and
              create a GDPR erasure request — our team will fully erase your personal data within one month.
              Some records (e.g. payment transactions, audit logs) may be retained where legally required.{' '}
              <a href="/privacy" className="font-medium text-slate-700 underline hover:text-slate-900">Learn more</a>.
            </p>
            <Button
              variant="outline"
              className="w-full sm:w-auto rounded-full h-11 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={() => setConfirmOpen(true)}
              data-testid="delete-account-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete account
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete your account?
            </DialogTitle>
            <DialogDescription>
              We&apos;ll deactivate your account immediately and create a GDPR erasure request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm text-slate-600">
            <p className="font-medium text-slate-900">What happens next:</p>
            <ol className="list-decimal pl-5 space-y-1.5">
              <li>You&apos;re signed out immediately and can no longer log in.</li>
              <li>A GDPR erasure request is created automatically.</li>
              <li>Our team reviews and approves it — your personal data is fully erased within <strong>one month</strong>, in line with UK GDPR.</li>
              <li>Certain records (payment transactions, audit logs) may be retained where legally required (e.g. HMRC 7-year tax retention, fraud-prevention audit trail).</li>
            </ol>
            <p className="text-xs text-slate-500 pt-1">
              Questions? Email <a href="mailto:support@shiftcover365.co.uk" className="font-medium underline">support@shiftcover365.co.uk</a> — or read the <a href="/privacy" className="font-medium underline">Privacy Policy</a> for full detail.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmOpen(false)} disabled={deleting}>
              Keep account
            </Button>
            <Button
              variant="destructive"
              className="rounded-full bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-account-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Deactivate & request erasure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
